import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect } from 'react';
import type { Difficulty } from '@/types';
import styles from './QuizSettingsModal.module.scss';
import RefreshDotIcon from '@/assets/common/refresh-dot.svg?react';

interface QuizSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalQuestions: number;
    initialDistribution: Record<string, number>;
    initialDifficulty: Difficulty | 'mixed';
    initialIncludeMath: boolean;
    onSave: (settings: {
        distribution: Record<string, number>;
        difficulty: Difficulty | 'mixed';
        includeMath: boolean;
    }) => void;
}

export function QuizSettingsModal({
    isOpen,
    onClose,
    totalQuestions,
    initialDistribution,
    initialDifficulty,
    initialIncludeMath,
    onSave,
}: QuizSettingsModalProps) {
    const [distribution, setDistribution] = useState<Record<string, number>>(initialDistribution);
    const [difficulty, setDifficulty] = useState<Difficulty | 'mixed'>(initialDifficulty);
    const [includeMath, setIncludeMath] = useState(initialIncludeMath);

    useEffect(() => {
        if (isOpen) {
            setDistribution(initialDistribution);
            setDifficulty(initialDifficulty);
            setIncludeMath(initialIncludeMath);
        }
    }, [isOpen, initialDistribution, initialDifficulty, initialIncludeMath]);

    const currentTotal = Object.values(distribution).reduce((sum, val) => sum + val, 0);

    const handleDistributionChange = (type: string, value: string) => {
        if (value === '') {
            setDistribution(prev => ({
                ...prev,
                [type]: 0
            }));
            return;
        }

        const newVal = parseInt(value);
        if (!isNaN(newVal)) {
            setDistribution(prev => ({
                ...prev,
                [type]: Math.max(0, newVal)
            }));
        }
    };

    const handleResetToDefault = () => {
        // Default balanced distribution
        let mc, fib, mix, math, code;

        if (includeMath) {
            mc = Math.round(totalQuestions * 0.45);
            fib = Math.round(totalQuestions * 0.20);
            mix = Math.round(totalQuestions * 0.10);
            math = totalQuestions - mc - fib - mix;
            code = 0;
        } else {
            mc = Math.round(totalQuestions * 0.6);
            fib = Math.round(totalQuestions * 0.3);
            mix = totalQuestions - mc - fib;
            math = 0;
            code = 0;
        }

        setDistribution({
            multiple_choice: mc,
            fill_blank: fib,
            mix_match: mix,
            math: math,
            code: code
        });
    };

    const handleSave = () => {
        onSave({
            distribution,
            difficulty,
            includeMath,
        });
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className={styles.modal__backdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className={styles.modal}
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    >
                        <div className={styles.modal__header}>
                            <h2 className={styles.modal__title}>Quiz Generation Settings</h2>
                            <button className={styles.modal__close} onClick={onClose}>✕</button>
                        </div>

                        <div className={styles.modal__content}>
                            <section className={styles.modal__section}>
                                <h3 className={styles.modal__sectionTitle}>Question Distribution</h3>
                                <p className={styles.modal__sectionHint}>
                                    Total questions based on length: <strong>{totalQuestions}</strong>.
                                    Current: <strong className={currentTotal === totalQuestions ? styles.success : styles.error}>{currentTotal}</strong>
                                </p>

                                <div className={styles.modal__distributionGrid}>
                                    <div className={styles.modal__field}>
                                        <label>Multiple Choice</label>
                                        <input
                                            type="number"
                                            value={distribution.multiple_choice || ''}
                                            placeholder="0"
                                            onChange={(e) => handleDistributionChange('multiple_choice', e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.modal__field}>
                                        <label>Fill in the Blank</label>
                                        <input
                                            type="number"
                                            value={distribution.fill_blank || ''}
                                            placeholder="0"
                                            onChange={(e) => handleDistributionChange('fill_blank', e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.modal__field}>
                                        <label>Mix and Match</label>
                                        <input
                                            type="number"
                                            value={distribution.mix_match || ''}
                                            placeholder="0"
                                            onChange={(e) => handleDistributionChange('mix_match', e.target.value)}
                                        />
                                    </div>

                                    {includeMath && (
                                        <div className={styles.modal__field}>
                                            <label>Math Solving</label>
                                            <input
                                                type="number"
                                                value={distribution.math || ''}
                                                placeholder="0"
                                                onChange={(e) => handleDistributionChange('math', e.target.value)}
                                            />
                                        </div>
                                    )}


                                </div>

                                <div className={styles.modal__actionRow}>
                                    <button className={styles.modal__textButton} onClick={handleResetToDefault}>
                                        <span className={styles.modal__icon}><RefreshDotIcon width={16} height={16} /></span> Set Default Ratio
                                    </button>
                                </div>
                            </section>

                            <section className={styles.modal__section}>
                                <h3 className={styles.modal__sectionTitle}>Special Types</h3>
                                <div className={styles.modal__checkboxGroup}>
                                    <label className={styles.modal__checkbox}>
                                        <input
                                            type="checkbox"
                                            checked={includeMath}
                                            onChange={(e) => {
                                                setIncludeMath(e.target.checked);
                                                if (!e.target.checked) handleDistributionChange('math', '0');
                                            }}
                                        />
                                        <span>Include Math</span>
                                    </label>

                                </div>
                            </section>

                            <section className={styles.modal__section}>
                                <h3 className={styles.modal__sectionTitle}>Difficulty</h3>
                                <div className={styles.modal__difficultyToggle}>
                                    {(['easy', 'medium', 'hard', 'mixed'] as const).map((d) => (
                                        <button
                                            key={d}
                                            className={`${styles.modal__difficultyButton} ${difficulty === d ? styles['modal__difficultyButton--active'] : ''}`}
                                            onClick={() => setDifficulty(d)}
                                        >
                                            {d.charAt(0).toUpperCase() + d.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </section>
                        </div>

                        <div className={styles.modal__footer}>
                            <button
                                className={styles.modal__saveButton}
                                onClick={handleSave}
                                disabled={currentTotal !== totalQuestions}
                            >
                                {currentTotal === totalQuestions ? 'Save Settings' : `Need ${totalQuestions} Questions`}
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
